import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import AssignUsersDialog from "features/assignUsersDialog/AssignUsersDialog";
import { Grow } from "components/Grow";
import { PageToolbar } from "components/PageToolbar";
import { Placeholder } from "components/Placeholder";
import { SearchField } from "components/SearchField";
import {
  Claim,
  ServiceSummary,
  SortableColumns,
  SortOrder,
  User,
  UserFilters,
  UserWithClaims,
} from "data/interfaces";
import { useEffect, useState } from "react";
import RoleActions from "./RoleActions";
import RoleTable from "./RoleTable";
import { PersonAddAlt } from "@mui/icons-material";
import { getFormattedDate, sortUsersBy } from "data/functions";

interface RolesViewProps {
  users: User[];
  selectedService: ServiceSummary;
  onUpdateRolesForUsers: (
    usersToUpdate: User[],
    service: ServiceSummary,
    claims: Claim[],
  ) => void;
  onAssignUsers: (
    assignUserRequests: UserWithClaims[],
    service: ServiceSummary,
  ) => void;
}

const RolesView = ({
  users,
  selectedService,
  onUpdateRolesForUsers,
  onAssignUsers,
}: RolesViewProps) => {
  const [assignUsersDialogOpen, setAssignUsersDialogOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);

  const [order, setOrder] = useState<SortOrder>("asc");
  const [orderBy, setOrderBy] = useState<keyof SortableColumns>("name");
  const [page, setPage] = useState(0);

  const serviceUsers = users.filter(
    (user) =>
      user.services.findIndex(
        (s) =>
          s.serviceReference === selectedService.serviceReference &&
          s.licenseReference === selectedService.licenseReference,
      ) !== -1,
  );

  const sortedUsers = sortUsersBy(orderBy, order, serviceUsers);

  const [filters, setFilters] = useState<UserFilters>({ search: "" });
  const filteredUsers = sortedUsers.filter((user) => {
    let searchString = filters.search?.toLowerCase();
    return (
      searchString == null ||
      (user.name || "").toLowerCase().includes(searchString) ||
      user.email.toLowerCase().includes(searchString)
    );
  });

  useEffect(() => {
    setSelectedUsers([]);
    setFilters({ search: "" });
  }, [selectedService]);

  const handleSelect = (selected: boolean, user: User) => {
    setSelectedUsers(
      selected
        ? [...selectedUsers, user]
        : selectedUsers.filter((u) => u.id !== user.id),
    );
  };

  const handleSelectAll = (allSelected: boolean) => {
    setSelectedUsers(
      allSelected
        ? filteredUsers.filter(
            (user) =>
              user.services.findIndex(
                (s) => s.licenseReference === selectedService?.licenseReference,
              ) !== -1,
          )
        : [],
    );
  };

  const handleSort = (column: string) => {
    const isAlreadySelectedAndAscending = orderBy === column && order === "asc";
    setOrder(isAlreadySelectedAndAscending ? "desc" : "asc");
    setOrderBy(column as keyof SortableColumns);
    setPage(0);
  };

  const handleFilterChange = (filters: UserFilters) => {
    setFilters(filters);
    setPage(0);
    setSelectedUsers([]);
  };

  const handleAssignUsers = (assignUserRequests: UserWithClaims[]) => {
    setAssignUsersDialogOpen(false);
    onAssignUsers(assignUserRequests, selectedService);
  };

  const handleEnableAllRolesForUsers = (usersToHandle: User[]) => {
    const allClaimsForSelectedService = selectedService.serviceClaims.map(
      (sc) =>
        ({
          type: sc.claimType,
          value: sc.options.map((o) => o.claimValue),
        }) as Claim,
    );

    const usersToUpdate = usersToHandle.map((user) => {
      const userService = user.services.find(
        (s) => s.serviceReference === selectedService.serviceReference,
      );
      if (userService) {
        userService.claims = allClaimsForSelectedService;
        user.services = user.services.map((s) =>
          s.serviceReference === selectedService.serviceReference
            ? userService
            : s,
        );
      } else
        user.services.push({
          licenseReference: selectedService.licenseReference,
          serviceReference: selectedService.serviceReference,
          claims: allClaimsForSelectedService,
        });
      return user;
    });

    onUpdateRolesForUsers(
      usersToUpdate,
      selectedService,
      allClaimsForSelectedService,
    );
    handleSelectAll(false);
  };

  const handleDisableAllRolesForUsers = (usersToHandle: User[]) => {
    const usersToUpdate = usersToHandle.map((user) => {
      user.services = user.services.filter(
        (s) => s.serviceReference !== selectedService.serviceReference,
      );
      return user;
    });

    onUpdateRolesForUsers(usersToUpdate, selectedService, []);
    handleSelectAll(false);
  };

  return (
    <Box p={3}>
      <Paper sx={{ mb: 3, p: 3 }}>
        <Typography variant="h6">{selectedService.name}</Typography>
        <Typography variant="body2" sx={{ py: 1 }}>
          {selectedService.description}
        </Typography>
        <Typography variant="body2" sx={{ py: 1 }}>
          {selectedService.unlimitedSeats === true
            ? ""
            : `${selectedService.seatsTotal - selectedService.seatsUsed} of ${
                selectedService.seatsTotal
              } seats available`}
        </Typography>
        <Typography variant="body2">
          {selectedService.nextLicenseExpiresOn !== undefined &&
          selectedService.nextLicenseExpiresOn !== null
            ? `Expiry date ${getFormattedDate(
                selectedService.nextLicenseExpiresOn,
              )}`
            : ""}
        </Typography>
      </Paper>
      {selectedService.serviceClaims.length > 0 && (
        <Box>
          <PageToolbar>
            <Button
              color="secondary"
              variant="contained"
              startIcon={<PersonAddAlt />}
              onClick={() => setAssignUsersDialogOpen(true)}
            >
              Assign/Edit Users
            </Button>
            <Grow />
            <Stack spacing={2} direction="row">
              <RoleActions
                selectedUsers={selectedUsers}
                isOneTimeSeat={selectedService.oneTimeSeats}
                onEnableAllRoles={handleEnableAllRolesForUsers}
                onDisableAllRoles={handleDisableAllRolesForUsers}
              />
              <SearchField
                value={filters.search}
                onChange={(event: any) =>
                  handleFilterChange({ ...filters, search: event.target.value })
                }
              />
            </Stack>
          </PageToolbar>

          {users.length === 0 ? (
            <Placeholder>No users found</Placeholder>
          ) : (
            <RoleTable
              users={filteredUsers}
              selected={selectedUsers}
              service={selectedService}
              onSelect={handleSelect}
              onSelectAll={handleSelectAll}
              orderBy={orderBy}
              order={order}
              onSort={handleSort}
              page={page}
              onPageChange={(page: number) => setPage(page)}
            />
          )}

          {assignUsersDialogOpen && selectedService && (
            <AssignUsersDialog
              users={users}
              selectedService={selectedService}
              onConfirm={handleAssignUsers}
              onClose={() => setAssignUsersDialogOpen(false)}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default RolesView;
